import React, { useState, useEffect, useRef } from 'react';
import { flushSync } from 'react-dom';
import { Navbar, Nav, Container } from 'react-bootstrap';
import { Link, useLocation } from 'react-router-dom';
import { AnimatePresence, motion } from 'framer-motion';
import { FaBars, FaTimes } from 'react-icons/fa';
import { withPublicUrl } from '../utils/publicPath';

interface NavbarProps {
  isHomePage: boolean;
}

const MOBILE_NAV_Z = 1100;

const mobileListVariants = {
  hidden: { opacity: 0 },
  show: {
    opacity: 1,
    transition: { staggerChildren: 0.055, delayChildren: 0.14 },
  },
};

const mobileItemVariants = {
  hidden: { opacity: 0, y: 20 },
  show: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.42, ease: [0.22, 1, 0.36, 1] },
  },
};

const Navigation: React.FC<NavbarProps> = ({ isHomePage }) => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const [isMobileCovering, setIsMobileCovering] = useState(false);
  const location = useLocation();

  useEffect(() => {
    let rafId = 0;
    let pending = false;

    const handleScroll = () => {
      if (pending) return;
      pending = true;
      rafId = requestAnimationFrame(() => {
        pending = false;
        const offset = window.scrollY;
        setIsScrolled((prev) => {
          if (isHomePage) {
            const hero = document.querySelector('.hero-section');
            if (hero) {
              const bottom = hero.getBoundingClientRect().bottom;
              // Layout-based threshold: stable when mobile URL bar resizes innerHeight.
              if (!prev && bottom < 56) return true;
              if (prev && bottom > 96) return false;
              return prev;
            }
            if (!prev && offset > 400) return true;
            if (prev && offset < 320) return false;
            return prev;
          }
          const threshold = 50;
          if (!prev && offset > threshold) return true;
          if (prev && offset < threshold - 40) return false;
          return prev;
        });
      });
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    handleScroll();
    return () => {
      window.removeEventListener('scroll', handleScroll);
      cancelAnimationFrame(rafId);
    };
  }, [isHomePage]);

  const coverTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const scheduleClearMobileCovering = () => {
    if (coverTimerRef.current) clearTimeout(coverTimerRef.current);
    coverTimerRef.current = setTimeout(() => setIsMobileCovering(false), 120);
  };

  useEffect(() => {
    setIsOpen(false);
    scheduleClearMobileCovering();
    return () => {
      if (coverTimerRef.current) clearTimeout(coverTimerRef.current);
    };
  }, [location.pathname]);

  useEffect(() => {
    if (!isOpen) return undefined;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setIsOpen(false);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return undefined;
    const mq = window.matchMedia('(min-width: 992px)');
    const onBreakpoint = () => {
      if (mq.matches) setIsOpen(false);
    };
    mq.addEventListener('change', onBreakpoint);
    return () => mq.removeEventListener('change', onBreakpoint);
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return undefined;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prev;
    };
  }, [isOpen]);

  const handleMobileNavLinkClick = () => {
    if (isOpen) {
      flushSync(() => setIsMobileCovering(true));
    }
    setIsOpen(false);
    // Pathname may not change (e.g. Termin while already on /appointment); still clear the cover.
    scheduleClearMobileCovering();
  };

  /** Avoid replace-navigation + overlay/timer edge cases when the target route is already active. */
  const handleAppointmentNavClick = (e: React.MouseEvent) => {
    if (location.pathname === '/appointment') {
      e.preventDefault();
    }
  };

  const navLinks = [
    { path: '/', label: 'Startseite' },
    { path: '/leistungen', label: 'Leistungen' },
    { path: '/fahrzeuge', label: 'Fahrzeuge' },
    { path: '/ueber-uns', label: 'Über uns' },
    // { path: '/news', label: 'News' }, // disabled for launch
    { path: '/kontakt', label: 'Kontakt' },
  ];

  const closeMobileMenu = () => setIsOpen(false);

  return (
    <>
      {isMobileCovering && (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            backgroundColor: '#111111',
            zIndex: MOBILE_NAV_Z - 1,
            pointerEvents: 'none',
          }}
        />
      )}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            key="mobile-full-nav"
            id="mobile-full-nav-panel"
            className="mobile-full-nav"
            role="dialog"
            aria-modal="true"
            aria-label="Hauptnavigation"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.32, ease: [0.22, 1, 0.36, 1] }}
            style={{ zIndex: MOBILE_NAV_Z }}
          >
            <motion.button
              type="button"
              className="mobile-full-nav-backdrop"
              aria-label="Menü schließen"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.28 }}
              onClick={closeMobileMenu}
            />
            <div className="mobile-full-nav-inner">
              <header className="mobile-full-nav-header">
                <Link
                  to="/"
                  className="mobile-full-nav-brand"
                  onClick={handleMobileNavLinkClick}
                >
                  <img
                    src={withPublicUrl('/images/logos/logo.png')}
                    alt="CarTech"
                    height={36}
                  />
                </Link>
                <button
                  type="button"
                  className="mobile-full-nav-close"
                  aria-label="Menü schließen"
                  onClick={closeMobileMenu}
                >
                  <FaTimes size={22} />
                </button>
              </header>

              <motion.nav
                className="mobile-full-nav-list-wrap"
                variants={mobileListVariants}
                initial="hidden"
                animate="show"
              >
                <div className="mobile-full-nav-list">
                  {navLinks.map((link) => (
                    <motion.div key={link.path} variants={mobileItemVariants}>
                      <Link
                        to={link.path}
                        className={`mobile-full-nav-link ${location.pathname === link.path ? 'active' : ''}`}
                        onClick={handleMobileNavLinkClick}
                      >
                        {link.label}
                      </Link>
                    </motion.div>
                  ))}
                  <motion.div variants={mobileItemVariants} className="mobile-full-nav-cta-wrap">
                    <Link
                      to="/appointment"
                      className={`mobile-full-nav-cta ${location.pathname === '/appointment' ? 'active' : ''}`}
                      onClick={(e) => {
                        handleMobileNavLinkClick();
                        handleAppointmentNavClick(e);
                      }}
                    >
                      Termin vereinbaren
                    </Link>
                  </motion.div>
                </div>
              </motion.nav>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      <Navbar
        expand="lg"
        fixed="top"
        className={`navbar-custom ${isScrolled ? 'scrolled' : ''} ${isHomePage ? 'home-navbar' : ''}`}
      >
        <Container>
          <Navbar.Brand as={Link} to="/" className="d-flex align-items-center">
            <img
              src={withPublicUrl('/images/logos/logo.png')}
              alt="CarTech Logo"
              height={isScrolled ? '35px' : '40px'}
              className={`navbar-logo ${isHomePage && !isScrolled ? 'navbar-logo-hidden' : ''}`}
            />
          </Navbar.Brand>

          <button
            type="button"
            className="mobile-menu-trigger d-lg-none border-0 ms-auto"
            onClick={() => setIsOpen(true)}
            aria-expanded={isOpen}
            aria-controls="mobile-full-nav-panel"
          >
            <FaBars size={22} />
          </button>

          <div className="d-none d-lg-flex ms-auto align-items-center">
            <Nav className="navbar-nav-desktop">
              {navLinks.map((link) => (
                <Nav.Link
                  key={link.path}
                  as={Link}
                  to={link.path}
                  className={`nav-link ${location.pathname === link.path ? 'active' : ''}`}
                >
                  {link.label}
                </Nav.Link>
              ))}
              <Nav.Link
                as={Link}
                to="/appointment"
                className={`nav-link appointment-link ${location.pathname === '/appointment' ? 'active' : ''}`}
                onClick={handleAppointmentNavClick}
              >
                Termin
              </Nav.Link>
            </Nav>
          </div>
        </Container>
      </Navbar>
    </>
  );
};

export default Navigation;
